import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'

import useOeBlock from '@aytm/app/stats/page/question-views/oe/states/useOeBlock'
import { CN_FAMILY_LIB } from '@aytm/app/stats/page/question-views/cn/lib/family'
import { CN_PROPS_BUILDERS } from '@aytm/app/stats/page/question-views/cn/lib/props'
import useChartOnOverview from '@aytm/app/stats/page/question-views/cn/states/useChartOnOverview'

import Controls from '@aytm/app/stats/page/question-views/cn/components/Controls'

import ChartsView from '@aytm/app/stats/page/question-views/cn/components/views/ChartsView'
import VerbatimView from '@aytm/app/stats/page/question-views/oe/components/views/VerbatimView'
import WordCloudView from '@aytm/app/stats/page/question-views/oe/components/views/WordCloudView'

import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'

import { BLOCK_PROP_TYPES } from '@aytm/app/stats/page/question-views/oe/Block'
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

// Conversation. It is the open-ended layout plus a `chart` view mode -- the per-turn
// breakdown -- and its own controls; the quote and cloud surfaces are the family's.
export const CN_CHART_VIEW_MODES = ['sentiments', 'tags', 'chart']

export const BlockView = ({
  viewMode,
  components,
  chartHeight,
  controlsProps,
  verbatimProps,
  chartProps,
  tableProps,
  collapsed = false,
  missingTotals = false,
  answer,
  wcProps,
  chartsModelProps
}) => {
  // totals are still being computed page-wide: no controls, just a held-open frame
  if (missingTotals) return <ChartPlaceholder isLoading />

  return (
    <>
      {isComponentOn(components, 'viewModes') && <Controls {...controlsProps} />}
      {!collapsed && (
        <>
          {CN_CHART_VIEW_MODES.includes(viewMode) && (
            <ChartsView
              components={components}
              answer={answer}
              chartHeight={chartHeight}
              chartsModelProps={chartsModelProps}
              chartProps={chartProps}
              tableProps={tableProps}
            />
          )}
          {'verbatim' === viewMode && <VerbatimView components={components} {...verbatimProps} />}
          {'wordcloud' === viewMode && <WordCloudView {...wcProps} />}
        </>
      )}
    </>
  )
}

const Block = props => {
  const { position } = props

  const extra = useMemo(() => ({ position }), [position])

  const blockProps = useOeBlock({ ...props, lib: CN_FAMILY_LIB, extra }, CN_PROPS_BUILDERS)
  useChartOnOverview(blockProps)

  return (
    <BlockWrapper {...props}>
      <BlockView {...blockProps} chartHeight={props.chartHeight} />
    </BlockWrapper>
  )
}

Block.displayName = 'CnBlock'
BlockView.displayName = 'CnBlockView'

BlockView.propTypes = {
  viewMode: PropTypes.string.isRequired,
  components: PropTypes.object,
  chartHeight: PropTypes.number,
  answer: answerPropType,
  missingTotals: PropTypes.bool,
  collapsed: PropTypes.bool,
  controlsProps: PropTypes.object,
  verbatimProps: PropTypes.object,
  chartProps: PropTypes.object,
  tableProps: PropTypes.object,
  wcProps: PropTypes.object,
  chartsModelProps: PropTypes.object
}

Block.propTypes = { ...BLOCK_PROP_TYPES, position: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }

export default Block
